import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cx from "classnames";
import { Dropdown } from "react-bootstrap";
import { animated, useTransition } from "@react-spring/web";
import { nanoid } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Cropper, ReactCropperElement } from "react-cropper";
import useMeasure from "react-use-measure";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import size from "lodash/size";
import times from "lodash/times";

import Slider from "src/components/slider";
import Button from "src/components/button";
import Tooltip from "src/components/tooltip";
import Actions from "src/components/actionbar";
import InfiniteScroll from "src/components/infiniteScroll";
import EmptyMessage from "src/components/emptyMessage";
import SortByAction from "src/components/sortByAction";
import { POSITIONS } from "src/constants/banner";
import { fetchExternalMediaLibrary } from "src/redux/actions/mediaLibrary";
import {
  MediaLibrary,
  MediaLibrarySortField,
  MediaLibrarySortOrder,
} from "src/types/mediaLibrary";
import { SortOrder } from "src/types/common";
import { MediaFile } from "src/types/media";
import { MediaResponse } from "src/types/experiences/storyboardSettings/primitives";
import { parseMediaMetadata } from "src/utils/media.service";
import { useCleverstoryUploadMedia } from "src/query/settings/uploadMedia";
import {
  useBrandKitMediaQuery,
  useBrandKitPaletteIdQuery,
  useDeleteMediaMutation,
  useMyUploadsQuery,
} from "src/query/experiences/storyboardSettings/brandKitMedia";
import { BrandKitMediaItem } from "src/services/experiences/storyboardSettings/brandKitMedia";
import { isAbsoluteUrl } from "src/pages/experiences/utils/cleverstorySitesettings";

import styles from "./editBanner.module.scss";
import {
  IMAGE_MIME_TYPES,
  LibraryMediaType,
  SORT_OPTIONS,
  SOURCES,
  Source,
  VIDEO_MIME_TYPES,
  applyCropToImage,
  resolveFileToUpload,
  toCardThumbnail,
  toCdnUrl,
  toMediaResponse,
  toPexelsThumbnail,
} from "./constants";

interface EditSocialImageProps {
  image: MediaResponse | null | undefined;
  position?: string;
  isOpen: boolean;
  allowVideo?: boolean;
  // eslint-disable-next-line no-unused-vars
  onClose: (value: MediaResponse | null, position: string) => void;
}

// Fallback thumbnail shown when an upload preview image fails to load.
const DEFAULT_UPLOAD_PREVIEW = "/images/defaultIcons/DefaultBackground.jpeg";

const previewUrlCache = new Map<string, string>();

const rememberPreviewUrl = (id?: string, url?: string) => {
  if (!id || !url) return;
  // Only cache locally resolvable urls (blob:/data:) that won't hit the CDN.
  if (url.startsWith("blob:") || url.startsWith("data:")) {
    previewUrlCache.set(id, url);
  }
};

interface UploadState {
  previewUrl: string;
  progress: number;
  status: "uploading" | "done";
  type?: string;
  result?: MediaResponse;
}

const EditSocialImage: React.FC<EditSocialImageProps> = ({
  image,
  position: initialPosition,
  isOpen,
  allowVideo = false,
  onClose,
}) => {
  const initialMedia: MediaFile | null = image
    ? (() => {
        const cachedUrl = image.id ? previewUrlCache.get(image.id) : undefined;
        if (cachedUrl) {
          return {
            id: image.id || nanoid(6),
            entityType: "media-preview",
            type: image?.type ?? "image",
            full: cachedUrl,
            thumbnail: cachedUrl,
            key: undefined,
            width: image.width,
            height: image.height,
            contentType: image.contentType,
          } as MediaFile;
        }
        return {
          id: image.id || nanoid(6),
          entityType: "media-preview",
          type: image?.type ?? "image",
          full: toCdnUrl(image.url),
          thumbnail: toCdnUrl(image.url),
          key: isAbsoluteUrl(image.url) ? undefined : image.url,
          width: image.width,
          height: image.height,
          contentType: image.contentType,
        } as MediaFile;
      })()
    : null;

  const [editAction, setEditAction] = useState<
    "" | "edit" | "crop" | "position"
  >(image ? "" : "edit");
  const [cropData, setCropData] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [media, setMedia] = useState<MediaFile | null>(initialMedia);
  const [mediaType, setMediaType] = useState<string | undefined>(image?.type);
  const [position, setPosition] = useState(initialPosition || "center center");
  const [uploadedMedia, setUploadedMedia] = useState<MediaResponse | null>(
    null,
  );
  const [pendingExternalUpload, setPendingExternalUpload] = useState(false);
  const [pendingCropFile, setPendingCropFile] = useState<File | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const { storyboardId = "" } = useParams<{ storyboardId: string }>();
  const { mutateAsync: uploadMediaAsync } = useCleverstoryUploadMedia();

  const [upload, setUpload] = useState<UploadState | null>(null);
  const submitting = upload?.status === "uploading" || finalizing;

  const previewTransitions = useTransition(editAction !== "edit", {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const editTransitions = useTransition(editAction === "edit", {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleClick = (action: string) => {
    setEditAction(action as "" | "edit" | "crop" | "position");
  };

  const startUpload = async (file: File, preview: MediaFile) => {
    const previewUrl = preview.thumbnail || preview.full;
    setPendingExternalUpload(false);
    setUpload({
      previewUrl,
      progress: 0,
      status: "uploading",
      type: preview.type,
    });
    try {
      const response = await uploadMediaAsync({
        ...preview,
        file,
        storyboardId,
        onProgress: (percent: number) =>
          setUpload((prev) => (prev ? { ...prev, progress: percent } : prev)),
      } as MediaFile & {
        storyboardId: string;
        // eslint-disable-next-line no-unused-vars
        onProgress: (percent: number) => void;
      });
      const uploaded = (response as any)?.response ?? response;

      rememberPreviewUrl(uploaded.id, previewUrl);

      setUpload({
        previewUrl,
        progress: 100,
        status: "done",
        type: uploaded.type ?? preview.type,
        result: {
          id: uploaded.id,
          name: uploaded.name ?? preview.name ?? "",
          contentType: uploaded.contentType ?? preview.contentType ?? "",
          type: uploaded.type ?? preview.type ?? "",
          width: uploaded.width ?? preview.width ?? 0,
          height: uploaded.height ?? preview.height ?? 0,
          url: uploaded.full ?? uploaded.url ?? uploaded.key ?? "",
        },
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload image");
      setUpload(null);
    }
  };

  const handleMediaSelect = (selected: MediaLibrary, external = false) => {
    if (!selected) return;

    const imageUrl = selected.imageUrl;
    if (!imageUrl) return;

    setMediaType(selected.mediaType);
    setMedia({
      id: selected.id,
      entityType: "media-preview",
      type: selected.mediaType,
      full: imageUrl,
      thumbnail: imageUrl,
      width: selected.width,
      height: selected.height,
    } as MediaFile);
    setUploadedMedia({
      id: selected.id,
      name: "",
      contentType: "",
      type: selected.mediaType,
      width: selected.width ?? 0,
      height: selected.height ?? 0,
      url: imageUrl,
    });
    setPendingExternalUpload(external);
    setCropData(null);
    setPendingCropFile(null);
    setEditAction("");
  };

  const handleExternalMediaSelect = (selected: MediaLibrary) =>
    handleMediaSelect(selected, true);

  const handleCroppedThumbnail = (value: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => {
    setCropData(value);
  };

  const handlePositionChange = (value: string) => {
    setPosition(value);
  };

  const finalizeAndClose = async () => {
    if (submitting) return;

    setFinalizing(true);

    const fileToUpload = await resolveFileToUpload({
      pendingCropFile,
      pendingExternalUpload,
      mediaUrl: media?.full,
    });

    if (fileToUpload && media) {
      try {
        const file = fileToUpload;
        const response = await uploadMediaAsync({
          ...media,
          file,
          name: media.name || file.name,
          contentType: file.type,
          storyboardId,
        } as MediaFile & { storyboardId: string });
        const uploaded = (response as any)?.response ?? response;

        rememberPreviewUrl(uploaded.id, media.full);
        const localPreview =
          media.full?.startsWith("blob:") || media.full?.startsWith("data:")
            ? media.full
            : undefined;
        onClose(
          {
            id: uploaded.id,
            name: uploaded.name ?? media.name ?? "",
            contentType: uploaded.contentType ?? file.type,
            type: uploaded.type ?? mediaType ?? "image",
            width: uploaded.width ?? media.width ?? 0,
            height: uploaded.height ?? media.height ?? 0,
            url:
              localPreview ??
              uploaded.full ??
              uploaded.url ??
              uploaded.key ??
              "",
          },
          position,
        );
      } catch (error) {
        console.error(error);
        toast.error("Failed to upload image");
        setFinalizing(false);
      }
      return;
    }

    // No upload needed — clear the loader and hand the existing media back.
    setFinalizing(false);
    onClose(uploadedMedia ?? toMediaResponse(media), position);
  };

  const handleConfirm = async () => {
    if (editAction === "crop") {
      if (media?.full && cropData && mediaType !== "video") {
        try {
          const cropped = await applyCropToImage(media.full, cropData);
          setMedia({
            ...media,
            full: cropped.url,
            thumbnail: cropped.url,
            key: undefined,
            width: cropped.width || media.width,
            height: cropped.height || media.height,
            contentType: cropped.contentType || media.contentType,
          } as MediaFile);
          if (cropped.blob) {
            const ext = (cropped.contentType.split("/")[1] || "png").split(
              "+",
            )[0];
            setPendingCropFile(
              new File(
                [cropped.blob],
                `${media.name || "crop"}-${Date.now()}.${ext}`,
                {
                  type: cropped.contentType || "image/png",
                },
              ),
            );
          }
          setCropData(null);
        } catch (error) {
          console.error(error);
          toast.error("Failed to apply crop");
          return;
        }
      }
      setEditAction("");
    } else if (editAction === "position") {
      setEditAction("");
    } else {
      await finalizeAndClose();
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    if (editAction === "crop" || editAction === "position") {
      setEditAction("");
    } else {
      onClose(null, position);
    }
  };

  const handleUploadedSelect = (uploaded: MediaResponse) => {
    setMediaType(uploaded.type);
    const cachedPreview = uploaded.id
      ? previewUrlCache.get(uploaded.id)
      : undefined;
    const displayUrl = cachedPreview ?? toCdnUrl(uploaded.url);
    setMedia({
      id: uploaded.id,
      entityType: "media-preview",
      type: (uploaded.type as "image" | "video") || "image",
      full: displayUrl,
      thumbnail: displayUrl,
      width: uploaded.width,
      height: uploaded.height,
      contentType: uploaded.contentType,
    } as MediaFile);
    setUploadedMedia(uploaded);
    setPendingExternalUpload(false);
    setCropData(null);
    setPendingCropFile(null);
    setEditAction("");
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const acceptedTypes = allowVideo
      ? [...IMAGE_MIME_TYPES, ...VIDEO_MIME_TYPES]
      : IMAGE_MIME_TYPES;
    if (!acceptedTypes.includes(file.type)) {
      toast.error(
        allowVideo
          ? "Unsupported file type. Please upload a supported image or video file."
          : "Unsupported file type. Please upload a supported image file.",
      );
      event.target.value = "";
      return;
    }
    const isVideo = file.type.startsWith("video/");
    const maxSizeMb = isVideo ? 50 : 10;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`Please upload a file of size less than ${maxSizeMb}MB`);
      event.target.value = "";
      return;
    }
    const thumbnailMedia = await parseMediaMetadata(file);
    if (!thumbnailMedia) return;
    event.target.value = "";
    startUpload(file, thumbnailMedia as MediaFile);
  };

  // Picker back button: keep current selection if any, otherwise close.
  const handlePickerBack = () => {
    if (media) {
      setEditAction("");
    } else {
      onClose(null, position);
    }
  };

  return (
    <Slider.Dialog
      className={styles.editBannerDialog}
      show={isOpen}
      width={
        editAction === "edit" || editAction === "crop" ? "50vw" : "380px"
      }
      onClose={() => {
        if (submitting) return;
        void finalizeAndClose();
      }}
    >
      <Slider.Body>
        <div className={styles.body}>
          {previewTransitions(
            (style, item) =>
              item && (
                <animated.div
                  style={style}
                  className={cx(styles.stack, {
                    [styles.stackVarient]:
                      editAction !== "edit" && editAction !== "crop",
                  })}
                >
                  <PreviewThumbnail
                    media={media}
                    crop={cropData}
                    position={position}
                    mediaType={mediaType}
                    editAction={editAction}
                    submitting={submitting}
                    defaultBanner={"/images/defaultIcons/DefaultBackground.jpeg"}
                    onSave={handleConfirm}
                    onActionChange={handleClick}
                    onClose={handleCancel}
                    onCropChange={handleCroppedThumbnail}
                    onPositionChange={handlePositionChange}
                  />
                </animated.div>
              ),
          )}

          {editTransitions(
            (style, item) =>
              item && (
                <animated.div
                  style={style}
                  className={cx(styles.stack, {
                    [styles.stackVarient]:
                      editAction !== "edit" && editAction !== "crop",
                  })}
                >
                  <ChooseMedia
                    upload={upload}
                    allowVideo={allowVideo}
                    onSelect={handleMediaSelect}
                    onExternalSelect={handleExternalMediaSelect}
                    onUploadedSelect={handleUploadedSelect}
                    onFileChange={handleFileChange}
                    onBack={handlePickerBack}
                  />
                </animated.div>
              ),
          )}
        </div>
      </Slider.Body>
    </Slider.Dialog>
  );
};

interface PreviewThumbnailProps {
  media: MediaFile | null;
  crop: { x: number; y: number; width: number; height: number } | null;
  position: string;
  mediaType?: string;
  editAction: "" | "edit" | "crop" | "position";
  submitting: boolean;
  defaultBanner: string;
  // eslint-disable-next-line no-unused-vars
  onActionChange: (action: string) => void;
  onSave: () => void;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onCropChange: (crop: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => void;
  // eslint-disable-next-line no-unused-vars
  onPositionChange: (position: string) => void;
}

interface PreviewMediaProps {
  media: MediaFile | null;
  position: string;
  defaultBanner: string;
}

const PreviewMedia: React.FC<PreviewMediaProps> = ({
  media,
  position,
  defaultBanner,
}) => {
  const [loaded, setLoaded] = useState(false);

  const isVideo = media?.type === "video";

  const previewUrl = media?.full || media?.thumbnail || defaultBanner;

  const backgroundStyles = position ? { objectPosition: position } : undefined;

  // Show the default banner when the media fails to load.
  const [failed, setFailed] = useState(false);

  // Reset to the loading state whenever the displayed media changes.
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [previewUrl]);

  const markLoaded = () => setLoaded(true);
  const markFailed = () => {
    setFailed(true);
    setLoaded(true);
  };

  return (
    <div className={styles.previewMedia}>
      {failed ? (
        <img
          src={defaultBanner}
          className={cx(styles.thumbnail)}
          style={backgroundStyles}
          alt=""
        />
      ) : isVideo ? (
        <video
          src={previewUrl}
          className={cx(styles.thumbnail)}
          style={backgroundStyles}
          controls={false}
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={markLoaded}
          onError={markFailed}
        />
      ) : (
        <img
          ref={(node) => {
            if (!node?.complete) return;
            if (node.naturalWidth === 0) markFailed();
            else markLoaded();
          }}
          src={previewUrl}
          className={cx(styles.thumbnail)}
          style={backgroundStyles}
          alt=""
          onLoad={markLoaded}
          onError={markFailed}
        />
      )}
      {!loaded && <div className={styles.previewSkeleton} />}
    </div>
  );
};

const PreviewThumbnail: React.FC<PreviewThumbnailProps> = ({
  media,
  crop,
  position,
  mediaType,
  editAction,
  submitting,
  defaultBanner,
  onActionChange,
  onSave,
  onClose,
  onCropChange,
  onPositionChange,
}) => {
  return (
    <div className={styles.preview}>
      <div className={styles.header}>
        <h2>customize your image</h2>
        <p>pick your preferred image to match your styles.</p>
      </div>
      <div
        className={cx(styles.previewBody, {
          [styles.previewVarient]: editAction !== "crop",
        })}
      >
        {editAction !== "crop" && (
          <PreviewMedia
            media={media}
            position={position}
            defaultBanner={defaultBanner}
          />
        )}

        {editAction === "crop" && (
          <CropBanner
            src={media?.full || ""}
            crop={crop}
            onCropChange={onCropChange}
          />
        )}

        {editAction === "position" && (
          <BannerPositionMenu
            position={position}
            handlePositionChange={onPositionChange}
          />
        )}

        {editAction !== "crop" && editAction !== "position" && (
          <EditToolbar
            show={true}
            mediaType={mediaType}
            onActionChange={onActionChange}
          />
        )}
      </div>
      <div className={styles.actions}>
        <Button
          variant="primary"
          icon={
            submitting ? "fa-solid fa-circle-notch fa-spin" : "paperflite-tick"
          }
          disabled={submitting}
          onClick={onSave}
        >
          {submitting
            ? "Saving..."
            : editAction === "crop" || editAction === "position"
              ? "Save"
              : "Confirm"}
        </Button>
        <Button variant="secondary" disabled={submitting} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

interface ChooseMediaProps {
  upload: UploadState | null;
  allowVideo?: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelect: (media: MediaLibrary) => void;
  // eslint-disable-next-line no-unused-vars
  onExternalSelect: (media: MediaLibrary) => void;
  // eslint-disable-next-line no-unused-vars
  onUploadedSelect: (media: MediaResponse) => void;
  // eslint-disable-next-line no-unused-vars
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBack: () => void;
}

const ChooseMedia: React.FC<ChooseMediaProps> = ({
  upload,
  allowVideo = false,
  onSelect,
  onExternalSelect,
  onUploadedSelect,
  onFileChange,
  onBack,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const acceptTypes = allowVideo
    ? [...IMAGE_MIME_TYPES, ...VIDEO_MIME_TYPES]
    : IMAGE_MIME_TYPES;

  return (
    <div className={styles.chooseMedia}>
      <div className={styles.mediaHeader}>
        <button className={styles.backButton} onClick={onBack}>
          <i className={cx("paperflite-left-arrow")}></i>
        </button>
        <div className={styles.header}>
          <h2>pick your {allowVideo ? "media" : "image"}</h2>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          accept={acceptTypes.join(",")}
          onChange={onFileChange}
        />
        <button
          className={styles.uploadButton}
          onClick={() => fileInputRef.current?.click()}
        >
          <i className={cx("paperflite-upload")}></i> Upload
        </button>
      </div>
      <div className={styles.mediaLibrary}>
        <MediaPicker
          allowVideo={allowVideo}
          onSelect={onSelect}
          onExternalSelect={onExternalSelect}
          upload={upload}
          onUploadedSelect={onUploadedSelect}
        />
      </div>
    </div>
  );
};

interface CropBannerProps {
  src: string;
  crop: { x: number; y: number; width: number; height: number } | null;
  // eslint-disable-next-line no-unused-vars
  onCropChange: (crop: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => void;
}

const CropBanner: React.FC<CropBannerProps> = ({
  src,
  crop: initialCrop,
  onCropChange,
}) => {
  const [editorRef, editorBounds] = useMeasure();
  const cropperRef = React.useRef<ReactCropperElement>(null);

  const [crop] = React.useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(initialCrop || null);

  return (
    <div ref={editorRef} className={styles.cropBanner}>
      <Cropper
        ref={cropperRef}
        src={src}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
        }}
        zoomable={false}
        viewMode={1}
        guides={false}
        minContainerHeight={editorBounds.height}
        ready={() => {
          if (crop) {
            cropperRef.current!.cropper.setData(crop!);
          }
        }}
        data={
          crop
            ? {
                x: crop?.x || 0,
                y: crop?.y || 0,
                width: crop?.width || 100,
                height: crop?.height || 100,
              }
            : undefined
        }
        autoCrop={true}
        autoCropArea={1}
        crop={() => {
          if (cropperRef.current) {
            const croppedData = cropperRef.current.cropper.getData();
            onCropChange(croppedData);
          }
        }}
      />
    </div>
  );
};

interface EditToolbarProps {
  show: boolean;
  mediaType?: string;
  // eslint-disable-next-line no-unused-vars
  onActionChange: (action: string) => void;
}

const EditToolbar: React.FC<EditToolbarProps> = ({
  show,
  mediaType,
  onActionChange,
}) => {
  const transitions = useTransition(show, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 },
  });

  const type = mediaType || "image";

  return transitions((style, item) =>
    item ? (
      <animated.div style={style} className={cx(styles.toolbar)}>
        <Tooltip content="Edit" placement="top">
          <div
            className={cx(styles.item)}
            onClick={() => onActionChange("edit")}
          >
            <i className={cx(styles.icon, "paperflite-pen")}></i>
          </div>
        </Tooltip>
        {type === "image" && (
          <Tooltip content="Crop" placement="top">
            <div
              className={cx(styles.item)}
              onClick={() => onActionChange("crop")}
            >
              <i className={cx(styles.icon, "paperflite-crop")}></i>
            </div>
          </Tooltip>
        )}

        <Tooltip content="Position" placement="top">
          <div
            className={cx(styles.item)}
            onClick={() => onActionChange("position")}
          >
            <i className={cx(styles.icon, "paperflite-focus")}></i>
          </div>
        </Tooltip>
      </animated.div>
    ) : null,
  );
};

interface BannerPositionMenuProps {
  position: string;
  // eslint-disable-next-line no-unused-vars
  handlePositionChange: (value: string) => void;
}

const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({
  position,
  handlePositionChange,
}) => {
  const transitions = useTransition(true, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 },
    delay: 500,
  });

  return transitions((style, item) =>
    item ? (
      <animated.div style={style} className={styles.positionMenu}>
        <Actions.Container>
          <Actions.Row className={styles.positionActionRow}>
            <Actions.Item className={styles.positions}>
              {POSITIONS.map((p) => {
                return (
                  <div
                    key={p.value}
                    className={cx(styles.position, {
                      [styles.active]: p.value === position,
                    })}
                    onClick={() => handlePositionChange(p.value)}
                  />
                );
              })}
            </Actions.Item>
          </Actions.Row>
        </Actions.Container>
      </animated.div>
    ) : null,
  );
};

interface MediaPickerProps {
  allowVideo?: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelect: (media: MediaLibrary) => void;
  // eslint-disable-next-line no-unused-vars
  onExternalSelect: (media: MediaLibrary) => void;
  upload: UploadState | null;
  // eslint-disable-next-line no-unused-vars
  onUploadedSelect: (media: MediaResponse) => void;
}

const MEDIA_TYPE_OPTIONS: { label: string; value: LibraryMediaType }[] = [
  { label: "Images", value: "image" },
  { label: "Videos", value: "video" },
];

const MediaPicker: React.FC<MediaPickerProps> = ({
  allowVideo = false,
  onSelect,
  onExternalSelect,
  upload,
  onUploadedSelect,
}) => {
  const [source, setSource] = useState<Source>("my-uploads");
  const [mediaType, setMediaType] = useState<LibraryMediaType>("image");
  const [query, setQuery] = useState("");
  const [sortField, setSortField] =
    useState<MediaLibrarySortField>("modifiedDate");
  const [sortOrder, setSortOrder] = useState<MediaLibrarySortOrder>("desc");

  useEffect(() => {
    if (!upload) return;
    setSource("my-uploads");

    if (upload.type === "image" || upload.type === "video") {
      setMediaType(upload.type);
    }
  }, [upload]);

  const handleSourceChange = (value: string) => {
    const next = value as Source;
    if (next === source) return;
    setSource(next);
    setMediaType("image");
    setQuery("");
    setSortField("modifiedDate");
    setSortOrder("desc");
  };

  return (
    <div className={styles.picker}>
      <div className={styles.pickerHeader}>
        <SelectMenu
          selected={source}
          options={SOURCES}
          onChange={handleSourceChange}
        />
        {allowVideo && (
          <SelectMenu
            selected={mediaType}
            options={MEDIA_TYPE_OPTIONS}
            onChange={(v) => setMediaType(v as LibraryMediaType)}
          />
        )}
        {source !== "brand-media" && (
          <>
            <SearchField
              key={source}
              value={query}
              placeholder={source === "pexels" ? "Search in Pexels" : "Search"}
              onSubmit={setQuery}
            />
            {source === "my-uploads" && (
              <SortByAction
                value={{
                  sortBy: sortField,
                  orderBy: sortOrder === "asc" ? SortOrder.ASC : SortOrder.DESC,
                }}
                onChange={(value) => {
                  setSortField(value.sortBy as MediaLibrarySortField);
                  setSortOrder(
                    value.orderBy === SortOrder.ASC ? "asc" : "desc",
                  );
                }}
                sortOptions={SORT_OPTIONS}
                classnames={styles.sortByActionWrapper}
              />
            )}
          </>
        )}
      </div>

      <div className={styles.pickerBody}>
        {source === "my-uploads" && (
          <MyUploadsList
            mediaType={mediaType}
            query={query}
            sort={sortField}
            order={sortOrder}
            onSelect={onSelect}
            upload={upload}
            onUploadedSelect={onUploadedSelect}
          />
        )}
        {source === "pexels" && (
          <PexelsList
            mediaType={mediaType}
            query={query}
            onSelect={onExternalSelect}
          />
        )}
        {source === "brand-media" && (
          <BrandMediaList mediaType={mediaType} onSelect={onSelect} />
        )}
      </div>
    </div>
  );
};

interface PexelsListProps {
  mediaType: LibraryMediaType;
  query: string;
  // eslint-disable-next-line no-unused-vars
  onSelect: (media: MediaLibrary) => void;
}

const PexelsList: React.FC<PexelsListProps> = ({
  mediaType,
  query,
  onSelect,
}) => {
  const dispatch = useDispatch();

  const formattedQuery = useMemo(
    () => (query || "design").replace(/\s+/g, "%20"),
    [query],
  );

  const { items, loading, storedMediaType, storedQuery, hasMore, nextPage } =
    useSelector((state) => {
      const lib = get(state, `mediaLibrary['pexels']`);
      const items = get(lib, "items") || [];
      const loading = get(lib, "loading");
      const storedMediaType = get(lib, "mediaType");
      const storedQuery = get(lib, "query");
      const pagination = get(lib, "pagination");
      const total = get(pagination, "totalPages", 0);
      const hasMore = get(pagination, "hasMoreItems");
      let next: number | null = get(pagination, "currentPage", 0) + 1;
      if (next != null && next >= total) next = null;
      return {
        items,
        loading,
        storedMediaType,
        storedQuery,
        hasMore,
        nextPage: next,
      };
    }, isEqual);

  const decode = (q?: string | null) => {
    try {
      return decodeURIComponent(q ?? "");
    } catch {
      return q ?? "";
    }
  };
  const isStale =
    storedMediaType !== mediaType ||
    decode(storedQuery) !== decode(formattedQuery);

  useEffect(() => {
    if (isStale && !loading) {
      dispatch(
        fetchExternalMediaLibrary({
          mediaType,
          mediaLibraryType: "pexels",
          orientation: "landscape",
          query: formattedQuery,
          page: 0,
          limit: 40,
        }),
      );
    }
  }, [mediaType, formattedQuery, isStale, loading, dispatch]);

  const handleLoadMore = () => {
    if (hasMore && nextPage != null) {
      dispatch(
        fetchExternalMediaLibrary({
          mediaType,
          mediaLibraryType: "pexels",
          orientation: "landscape",
          query: formattedQuery,
          page: nextPage,
          limit: 40,
        }),
      );
    }
  };

  if ((loading || isStale) && size(items) === 0) {
    return <MediaSkeletonGrid />;
  }

  if (!loading && !isStale && size(items) === 0) {
    return (
      <div className={styles.empty}>
        <EmptyMessage
          width={320}
          image={"/images/common/banner/sources/pexel-empty-search.svg"}
          message={
            "Don't panic, nothing's broken, just couldn't find what you are looking for. Try a different term."
          }
        />
      </div>
    );
  }

  return (
    <InfiniteScroll
      className={styles.grid}
      style={{ gridTemplateColumns: `repeat(4, minmax(100px, 1fr))` }}
      loading={loading}
      hasMore={!isStale && hasMore}
      loadMore={handleLoadMore}
      loader={() => <SkeletonCards count={2} />}
    >
      {items.map((m: MediaLibrary) => (
        <MediaCard key={m.id} item={m} onClick={() => onSelect(m)} />
      ))}
    </InfiniteScroll>
  );
};

interface MyUploadsListProps {
  mediaType: LibraryMediaType;
  query: string;
  sort: MediaLibrarySortField;
  order: MediaLibrarySortOrder;
  // eslint-disable-next-line no-unused-vars
  onSelect: (media: MediaLibrary) => void;
  upload: UploadState | null;
  // eslint-disable-next-line no-unused-vars
  onUploadedSelect: (media: MediaResponse) => void;
}

const MyUploadsList: React.FC<MyUploadsListProps> = ({
  mediaType,
  query,
  sort,
  order,
  onSelect,
  upload,
  onUploadedSelect,
}) => {
  const { storyboardId = "" } = useParams<{ storyboardId: string }>();

  const { data, isFetching, hasNextPage, loadMore, isFetchingNextPage } =
    useMyUploadsQuery(storyboardId, mediaType, sort, order, query);

  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const { mutateAsync: deleteMedia } = useDeleteMediaMutation();

  const items = useMemo(
    () => data?.pages.flatMap((p) => p.records) ?? [],
    [data],
  );

  const handleDelete = async (id: string) => {
    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      await deleteMedia(id);
      setDeletedIds((prev) => new Set(prev).add(id));
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleSelect = (item: BrandKitMediaItem) => {
    const imageUrl = toCdnUrl(item.url);
    onSelect({
      id: item.id,
      imageUrl,
      thumbnailUrl: imageUrl,
      mediaType: item.type,
      width: item.width,
      height: item.height,
    } as unknown as MediaLibrary);
  };

  const uploadedInList =
    upload?.status === "done" &&
    !!upload.result &&
    items.some((item) => item.id === upload.result?.id);

  // Don't resurrect the upload preview card once its uploaded media was deleted.
  const uploadDeleted =
    !!upload?.result?.id && deletedIds.has(upload.result.id);

  const uploadMatchesTab = !upload?.type || upload.type === mediaType;

  const uploadCard =
    upload && uploadMatchesTab && !uploadedInList && !uploadDeleted ? (
      <UploadCard
        upload={upload}
        onSelect={onUploadedSelect}
        onDelete={handleDelete}
        isDeleting={!!upload.result?.id && deletingIds.has(upload.result.id)}
      />
    ) : null;

  if (isFetching && items.length === 0) {
    return (
      <div
        className={styles.grid}
        style={{ gridTemplateColumns: `repeat(4, minmax(100px, 1fr))` }}
      >
        {uploadCard}
        <SkeletonCards count={6} />
      </div>
    );
  }

  if (!isFetching && items.length === 0 && !uploadCard) {
    return (
      <div className={styles.empty}>
        <EmptyMessage
          width={320}
          image={"/images/common/banner/sources/pexel-empty-search.svg"}
          message={"No media here yet. Upload one to get started."}
        />
      </div>
    );
  }

  return (
    <InfiniteScroll
      className={styles.grid}
      style={{ gridTemplateColumns: `repeat(4, minmax(100px, 1fr))` }}
      loading={isFetchingNextPage}
      hasMore={hasNextPage}
      loadMore={loadMore}
      loader={() => <SkeletonCards count={2} />}
    >
      {uploadCard}
      {items.map((item) => (
        <BrandMediaCard
          key={item.id}
          item={item}
          onClick={() => handleSelect(item)}
          onDelete={handleDelete}
          isDeleting={deletingIds.has(item.id)}
        />
      ))}
    </InfiniteScroll>
  );
};

// Indeterminate progress bar shown over a card while its media is being deleted.
const DeletingOverlay: React.FC = () => (
  <div className={styles.uploadOverlay}>
    <div className={styles.uploadProgressTrack}>
      <div className={cx(styles.uploadProgressFill, styles.deletingFill)} />
    </div>
  </div>
);

interface CardDeleteButtonProps {
  // eslint-disable-next-line no-unused-vars
  onDelete: (id: string) => void;
  id: string;
}

// Hover-revealed delete control rendered in the top-right of a media card.
const CardDeleteButton: React.FC<CardDeleteButtonProps> = ({
  onDelete,
  id,
}) => (
  <div className={styles.cardOverlay}>
    <button
      className={styles.deleteBtn}
      onClick={(e) => {
        e.stopPropagation();
        onDelete(id);
      }}
    >
      <i className="paperflite-delete" />
    </button>
  </div>
);

interface UploadCardProps {
  upload: UploadState;
  // eslint-disable-next-line no-unused-vars
  onSelect: (media: MediaResponse) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
}

const UploadCard: React.FC<UploadCardProps> = ({
  upload,
  onSelect,
  onDelete,
  isDeleting,
}) => {
  const isDone = upload.status === "done";
  const uploadedId = upload.result?.id;
  // Fall back to the default preview when the media fails to load.
  const [failed, setFailed] = useState(false);

  return (
    <div
      className={cx(styles.mediaCard, styles.uploadCard, {
        [styles.uploadCardDone]: isDone,
        [styles.deletingCard]: isDeleting,
      })}
      onClick={() => {
        if (isDone && upload.result && !isDeleting) onSelect(upload.result);
      }}
    >
      {failed ? (
        <img src={DEFAULT_UPLOAD_PREVIEW} alt="uploading" />
      ) : upload.type === "video" ? (
        <video
          src={upload.previewUrl}
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setFailed(true)}
        />
      ) : (
        <img
          src={upload.previewUrl}
          alt="uploading"
          onError={(e) => {
            const img = e.currentTarget;
            if (img.src.endsWith(DEFAULT_UPLOAD_PREVIEW)) return;
            img.src = DEFAULT_UPLOAD_PREVIEW;
          }}
        />
      )}
      {!isDone && (
        <div className={styles.uploadOverlay}>
          <div className={styles.uploadProgressTrack}>
            <div
              className={styles.uploadProgressFill}
              style={{ width: `${upload.progress}%` }}
            />
          </div>
        </div>
      )}
      {isDone && isDeleting && <DeletingOverlay />}
      {isDone && !isDeleting && onDelete && uploadedId && (
        <CardDeleteButton onDelete={onDelete} id={uploadedId} />
      )}
    </div>
  );
};

interface BrandMediaListProps {
  mediaType: LibraryMediaType;
  // eslint-disable-next-line no-unused-vars
  onSelect: (media: MediaLibrary) => void;
}

const BrandMediaList: React.FC<BrandMediaListProps> = ({
  mediaType,
  onSelect,
}) => {
  const { data: paletteId, isLoading: paletteLoading } =
    useBrandKitPaletteIdQuery();

  const { data, isFetching, hasNextPage, loadMore, isFetchingNextPage } =
    useBrandKitMediaQuery(paletteId ?? "", mediaType, Boolean(paletteId));

  const items = useMemo(
    () => data?.pages.flatMap((p) => p.records) ?? [],
    [data],
  );

  const handleSelect = (item: BrandKitMediaItem) => {
    const imageUrl = toCdnUrl(item.url);
    onSelect({
      id: item.id,
      imageUrl,
      thumbnailUrl: imageUrl,
      mediaType: item.type,
      width: item.width,
      height: item.height,
    } as unknown as MediaLibrary);
  };

  if ((paletteLoading || isFetching) && items.length === 0) {
    return <MediaSkeletonGrid />;
  }

  if (!isFetching && items.length === 0) {
    return (
      <div className={styles.empty}>
        <EmptyMessage
          width={320}
          image={"/images/common/banner/sources/pexel-empty-search.svg"}
          message={
            "No brand media found. Upload images to your brand kit to get started."
          }
        />
      </div>
    );
  }

  return (
    <InfiniteScroll
      className={styles.grid}
      style={{ gridTemplateColumns: `repeat(4, minmax(100px, 1fr))` }}
      loading={isFetchingNextPage}
      hasMore={hasNextPage}
      loadMore={loadMore}
      loader={() => <SkeletonCards count={2} />}
    >
      {items.map((item) => (
        <BrandMediaCard
          key={item.id}
          item={item}
          onClick={() => handleSelect(item)}
        />
      ))}
    </InfiniteScroll>
  );
};

interface MediaCardProps {
  item: MediaLibrary;
  onClick: () => void;
}

// Pexels result card — read-only, no delete affordance.
const MediaCard: React.FC<MediaCardProps> = ({ item, onClick }) => {
  return (
    <div className={styles.mediaCard} onClick={onClick}>
      <img
        src={toPexelsThumbnail(item.thumbnailUrl)}
        alt={item.id}
        loading="lazy"
        decoding="async"
        onError={(e) => {
          const img = e.currentTarget;
          if (img.src.endsWith(DEFAULT_UPLOAD_PREVIEW)) return;
          img.src = DEFAULT_UPLOAD_PREVIEW;
        }}
      />
    </div>
  );
};

interface BrandMediaCardProps {
  item: BrandKitMediaItem;
  onClick: () => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
}

const BrandMediaCard: React.FC<BrandMediaCardProps> = ({
  item,
  onClick,
  onDelete,
  isDeleting,
}) => {
  const url = toCdnUrl(item.url);
  const isVideo = item.type === "video";
  // Fall back to the default preview when the media fails to load.
  const [failed, setFailed] = useState(false);
  // A poster thumbnail if the API provides one (image-kit derived frame, etc.).
  const poster =
    typeof item.thumbnail === "string"
      ? toCdnUrl(item.thumbnail)
      : typeof item.icon === "string"
        ? toCdnUrl(item.icon)
        : undefined;

  return (
    <div
      className={cx(styles.mediaCard, { [styles.deletingCard]: isDeleting })}
      onClick={!isDeleting ? onClick : undefined}
    >
      {failed ? (
        <img src={DEFAULT_UPLOAD_PREVIEW} alt={item.name} />
      ) : isVideo ? (
        <video
          src={poster ? url : `${url}#t=0.1`}
          poster={poster ? toCardThumbnail(poster) : undefined}
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setFailed(true)}
        />
      ) : (
        <img
          src={toCardThumbnail(item.url)}
          alt={item.name}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
      {isDeleting ? (
        <DeletingOverlay />
      ) : (
        onDelete && <CardDeleteButton onDelete={onDelete} id={item.id} />
      )}
    </div>
  );
};

const SkeletonCards: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <>
    {times(count).map((i) => (
      <div key={i} className={styles.skeletonCard} />
    ))}
  </>
);

const MediaSkeletonGrid: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div
    className={styles.grid}
    style={{ gridTemplateColumns: `repeat(4, minmax(100px, 1fr))` }}
  >
    <SkeletonCards count={count} />
  </div>
);

interface SelectMenuProps {
  selected: string;
  options: { label: string; value: string; icon?: string }[];
  // eslint-disable-next-line no-unused-vars
  onChange: (value: string) => void;
  placeholder?: string;
}

const SelectMenu: React.FC<SelectMenuProps> = ({
  selected,
  options,
  onChange,
  placeholder,
}) => {
  const current = useMemo(
    () => options.find((o) => o.value === selected),
    [selected, options],
  );

  return (
    <Dropdown className={styles.selectDropdown}>
      <Dropdown.Toggle>
        {current ? (
          <>
            {current.icon && <img src={current.icon} alt={current.label} />}
            <span>{current.label}</span>
          </>
        ) : (
          <span>{placeholder}</span>
        )}
        <i className="paperflite-dropdown" />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {options.map((option) => {
          const active = option.value === selected;
          return (
            <Dropdown.Item
              key={option.value}
              className={cx({ [styles.active]: active })}
              onClick={() => onChange(option.value)}
            >
              {active ? (
                <i className="fa-solid fa-circle-check" />
              ) : (
                <i className="fa-regular fa-circle" />
              )}
              <span>{option.label}</span>
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

interface SearchFieldProps {
  value: string;
  placeholder: string;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (value: string) => void;
}

const SearchField: React.FC<SearchFieldProps> = ({
  value,
  placeholder,
  onSubmit,
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <form
      className={styles.searchField}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft);
      }}
    >
      <input
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
      />
      <i className="fa-solid fa-magnifying-glass" />
    </form>
  );
};

export { EditSocialImage };
